import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAddContainer } from "@/hooks/useContainers";
import { ApiError } from "@/lib/api";

/**
 * "Add domain to my account" modal. Surfaces the backend's validation +
 * pool-capacity errors inline so users learn why a request was rejected
 * (e.g. plan domain limit reached → 400 with code "domain_capacity_exceeded").
 */
export function AddContainerDialog() {
  const [open, setOpen] = React.useState(false);
  const [domain, setDomain] = React.useState("");
  const [registrar, setRegistrar] = React.useState("");
  const [registrarProvider, setRegistrarProvider] = React.useState("HosterDash");

  const navigate = useNavigate();
  const { toast } = useToast();
  const addContainer = useAddContainer();

  function reset() {
    setDomain("");
    setRegistrar("");
    setRegistrarProvider("HosterDash");
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      const created = await addContainer.mutateAsync({
        domain_name: domain.trim().toLowerCase(),
        registrar: registrar.trim() || null,
        registrar_provider: registrarProvider
      });
      toast({
        title: "Domain added",
        description: `${created.domain_name} is ready to configure.`,
        variant: "success"
      });
      setOpen(false);
      reset();
      navigate(`/domains/${created.id}`);
    } catch (err) {
      const description =
        err instanceof ApiError
          ? `${err.message}${err.code ? ` (${err.code})` : ""}`
          : "Couldn't add the domain.";
      toast({ title: "Add domain failed", description, variant: "destructive" });
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4" />
          Add domain
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a domain</DialogTitle>
          <DialogDescription>
            We'll set up an empty container — you can configure mailboxes, websites and SSL afterwards.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="domain">Domain name</Label>
            <Input
              id="domain"
              required
              autoFocus
              autoComplete="off"
              spellCheck={false}
              placeholder="example.com"
              value={domain}
              onChange={e => setDomain(e.target.value)}
              inputMode="url"
            />
            <p className="text-xs text-muted-foreground">
              Lowercase. Must already be registered — we don't register new TLDs here yet.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="registrarProvider">Where is it registered?</Label>
              <select
                id="registrarProvider"
                value={registrarProvider}
                onChange={e => setRegistrarProvider(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="HosterDash">HosterDash</option>
                <option value="GoDaddy">GoDaddy</option>
                <option value="Namecheap">Namecheap</option>
                <option value="Cloudflare">Cloudflare</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="registrar">Display name <span className="font-normal text-muted-foreground">(optional)</span></Label>
              <Input
                id="registrar"
                placeholder="e.g. GoDaddy account 1"
                value={registrar}
                onChange={e => setRegistrar(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={addContainer.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={addContainer.isPending || !domain.trim()}>
              {addContainer.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Add domain
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
